import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import safeStorage from '../utils/safeStorage';

const AuthContext = createContext();

// API URL configuration - uses consolidated server
// In production: uses relative URLs (nginx proxies to backend)
// In local dev mode (port 3002): uses consolidated server on port 3000
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(safeStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const [publicScreensLoaded, setPublicScreensLoaded] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [publicScreens, setPublicScreens] = useState([]);
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionExpiredRef = useRef(false);

  const fetchPermissions = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/auth/permissions`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || {});
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions({});
    }
  };

  const fetchPublicScreens = async () => {
    try {
      const response = await fetch(`${API_URL}/public/screens`);
      
      if (response.ok) {
        const data = await response.json();
        setPublicScreens(data.publicScreens || []);
      }
    } catch (error) {
      console.error('Error fetching public screens:', error);
      setPublicScreens([]);
    } finally {
      setPublicScreensLoaded(true);
    }
  };

  const isPublicScreen = async (screenPath) => {
    try {
      const response = await fetch(`${API_URL}/public/screen/${encodeURIComponent(screenPath)}`);
      
      if (response.ok) {
        const data = await response.json();
        return data.isPublic;
      }
      return false;
    } catch (error) {
      console.error('Error checking if screen is public:', error);
      return false;
    }
  };

  // Global fetch interceptor — fires session-expired when any authenticated API call returns 401
  useEffect(() => {
    if (!token) return;

    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const res = await originalFetch(...args);

      if (res.status === 401 && !sessionExpiredRef.current) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/me') || url.includes('/auth/logout') || url.includes('/auth/refresh') || url.includes('/auth/tos-status') || url.includes('/auth/accept-tos');
        if (!isAuthEndpoint) {
          sessionExpiredRef.current = true;
          setUser(null);
          setToken(null);
          setPermissions({});
          safeStorage.removeItem('authToken');
          setSessionExpired(true);
        }
      }

      return res;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [token]);

  useEffect(() => {
    // Always fetch public screens, regardless of authentication
    fetchPublicScreens();

    if (token) {
      // Verify token and get user info
      fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          safeStorage.removeItem('authToken');
          setToken(null);
          throw new Error('Invalid token');
        }
      })
      .then(userData => {
        setUser(userData);
        // Fetch permissions after user is set
        fetchPermissions(token);
      })
      .catch(error => {
        console.error('Authentication error:', error);
        setUser(null);
        setToken(null);
        setPermissions({});
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.requirePasswordChange) {
          return { requirePasswordChange: true, userId: data.userId };
        }
        
        setToken(data.token);
        setUser(data.user);
        safeStorage.setItem('authToken', data.token);
        sessionExpiredRef.current = false;
        setSessionExpired(false);
        // Fetch permissions after successful login
        await fetchPermissions(data.token);
        // Prefer the user's saved landing page over the role-based default screen
        return { success: true, defaultScreenPath: data.user?.landing_page || data.defaultScreenPath };
      } else {
        // Check for email verification required
        if (data.requiresVerification) {
          const error = new Error(data.message || 'Email not verified');
          error.requiresVerification = true;
          error.email = data.email;
          throw error;
        }
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setPermissions({});
      safeStorage.removeItem('authToken');
    }
  };

  // Check if user has specific permission for a screen
  const hasPermission = (screenPath, permission) => {
    // Admin users have all permissions
    if (user?.is_admin) {
      return true;
    }

    const screenPermissions = permissions[screenPath];
    if (!screenPermissions) {
      return false;
    }

    return screenPermissions.permissions.includes(permission);
  };

  // Check if user can access a screen (has any permission OR screen is public)
  const canAccessScreen = (screenPath) => {
    // Check if screen is public first
    const isPublic = publicScreens.some(screen => screen.screen_path === screenPath);
    if (isPublic) {
      return true;
    }

    // Admin users can access all screens
    if (user?.is_admin) {
      return true;
    }

    return !!permissions[screenPath];
  };

  // Check if user has readonly access to a screen
  const isReadOnly = (screenPath) => {
    // Admin users have full access (not read-only)
    if (user?.is_admin) {
      return false;
    }

    const screenPermissions = permissions[screenPath];
    if (!screenPermissions) {
      return true; // No access means effectively read-only
    }

    // If user only has readonly permission and not update permission
    return screenPermissions.permissions.includes('readonly') && 
           !screenPermissions.permissions.includes('update');
  };

  // Helper function to check if user is accessing amplifier details without authentication
  const isUnauthenticatedAmplifierAccess = () => {
    return !user && (window.location.pathname.startsWith('/amplifier-components/') || window.location.pathname.startsWith('/amps/'));
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  const forceChangePassword = async (userId, currentPassword, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/auth/force-change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, currentPassword, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  // Refresh user data from the server (useful after profile updates)
  const refreshUser = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const clearSessionExpired = () => {
    sessionExpiredRef.current = false;
    setSessionExpired(false);
  };

  // Silent token refresh — renew 1 hour before expiry so active sessions never expire
  useEffect(() => {
    if (!token) return;

    const getTokenExpiry = (tok) => {
      try {
        const payload = JSON.parse(atob(tok.split('.')[1]));
        return payload.exp * 1000;
      } catch {
        return null;
      }
    };

    const exp = getTokenExpiry(token);
    if (!exp) return;

    const refreshIn = exp - Date.now() - 60 * 60 * 1000; // 1 hour before expiry
    if (refreshIn <= 0) return; // already in the last hour — let it expire naturally

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const { token: newToken } = await res.json();
          setToken(newToken);
          safeStorage.setItem('authToken', newToken);
        }
      } catch {
        // Silent failure — user will get the session-expired modal on their next action
      }
    }, refreshIn);

    return () => clearTimeout(timer);
  }, [token]);

  const updateLandingPage = async (path) => {
    try {
      const response = await fetch(`${API_URL}/users/me/landing-page`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ landing_page: path })
      });
      if (response.ok) {
        setUser(prev => ({ ...prev, landing_page: path }));
      }
    } catch (error) {
      console.error('Error updating landing page:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    publicScreensLoaded,
    permissions,
    publicScreens,
    sessionExpired,
    clearSessionExpired,
    login,
    logout,
    changePassword,
    forceChangePassword,
    refreshUser,
    updateLandingPage,
    hasPermission,
    canAccessScreen,
    isReadOnly,
    isPublicScreen,
    isUnauthenticatedAmplifierAccess,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.is_admin || false,
    isBillingSupport: user?.is_billing_support || false,
    isModerator: user?.is_admin || user?.is_site_moderator || user?.is_board_moderator || false,
    isSiteModerator: user?.is_site_moderator || false,
    isBoardModerator: user?.is_board_moderator || false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
